import Vue from 'vue';
import axios from 'axios';
import VueAxios from 'vue-axios'
import store from './vuex/store';
import App from './components/App.vue';
import Login from './components/Login.vue';

import translations from './translations/translations';

import PDFModel from './pdf/reportModel.js';


// debugging variable.
window.ftig = 0;


/**
 * OBJECT tables]
 * @type {Object}
 *
 * This object is designed to house calculated data tables, which are used in 
 * various components. The strategy with setting this as global objects that
 * are children of the window object is to break these OUT of the Vuex store, 
 * eliminating the risk of recursive components updates that can cause procedural
 * loops that can crash the browser.
 */
window.tables = {

  legends: {},

  netWorth: {},

  retirementSummary: {},
  wealthAccumulationData: [],
  systematicWithdrawalsData: [],
  postRetirementCashFlowData: [],

  financialGPSSummary: {},

  toolSavingsFuture: {},
  toolInvestmentFuture: {},
  toolRegularWithdrawls: {},
  toolSystematicWithdrawals: {},

}


/**
 * timeOut variables
 * Defining variables to be used for setTimeout calls in the application
 */

// Used by auto save functionality
window.autoSave = false;

// Used by reset data functionality
window.resetData = false;

// This array defines the values that will be cut over to snapshots.
window._dataContextSegments = [
  "assets",
  "debt",
  "income",
  "insurance",
  "retirement",
  "savings",
];


/** 
 * PROTOTYPE: toCustomString()
 * @return {string} the custom formatted date string
 *
 * This function gets added to the default Date object, allowing us to create
 * a custom output, while accounting for the active language.
 */
Date.prototype.toCustomString = function() {

  var lang = document.getElementsByTagName('html')[0].getAttribute('lang');

  var months = (lang === 'fr') ? 
    ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'] : 
    ['January','February','March','April','May','June','July','August','September','October','November','December'];    

  var m = months[this.getMonth()]; 
  var d = this.getDate();

  return (lang === 'fr') ? d + ' ' + m + ' ' + this.getFullYear() : m + ' ' + d + ', ' + this.getFullYear();

};


/** 
 * ---------------------------------------------------------------------------- /
 * GLOBAL FUNCTIONS
 *
 * Here, we're defining a series of helper functions that are intended to be 
 * globally available. These functions are added directly to the window object.
 * This is done more as a convention to indicate that these are global functions,
 * as globally defined functions are added to the window object anyhow.
 *
 * --------------------------------------------------------------------------- */

/**
 * FUNCTION: rawObject()
 * @param  {Object} obj - the object to be converted.
 * @return {Object} the processed object
 *
 * This function is intended as a means of converting a Vue object into a standard
 * JavaScript object. Most often, we'll want to do this to get around some of the
 * magic caching-type stuff that Vue seems to do with its objects, or to make it
 * possible to output the actual data to the console (since Vue objects tend to
 * be obfuscated).
 */
window.rawObject = (obj) => {

  return JSON.parse(JSON.stringify(obj));

},


/**
 * FUNCTION: obscureName()
 * @param  {string} name - the name to be obfuscated
 * @return {string} the obfuscated name
 *
 * This function works to obscure client names for display within the application.
 * Its primary use is in the client search tool, so that all client names are not
 * appearing in their entirety.
 */
window.obscureName = (name) => {

  var o = name.substr(0,2);

  for ( var x = 1; x < name.length; x++ ) {

    o += '<span class="c">&bullet;</span>';

  }

  return o;

}


/**
 * FUNCTION: getLanguage();
 * @return {string} the language code
 *
 * This function is designed as a fallback function for determining the current
 * language of the application. Ideally, most requests for this sort of information
 * would pull the code out of the store, but there are a few isolated instances,
 * such as the PDF generator, that are operating somewhat independently of Vue.
 */
window.getLanguage = () => {
  return document.getElementsByTagName('html')[0].getAttribute('lang');
}


/**
 * FUNCTION: thousandSeparators()
 * @param  {string} x - the numeric string to be converted.
 * @return {string} the converted string
 *
 * This global function is designed to convert numeric strings to include 
 * thousand separators. It checks for the current language and adds the appropriate
 * value (,) OR ( ).
 */
window.thousandSeparators = (x) => {
  var lang = window.getLanguage();
  if ( lang === 'fr' ) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  } else {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
}


/**
 * FUNCTION: formatCurrency()
 * @param  {string} val - the numeric string to be converted.
 * @param  {boolean} nspan - defines whether negative values should be wrapped in a span.
 * @return {string} the converted string
 *
 * This global function is designed to convert numeric values into currency
 * formatted string. The function checks for the current language to determine the
 * currency format. The function will also optionally attempt to wrap the entire value
 * in a span, which can be used for visual formatting on the front-end.
 */
window.formatCurrency = (val, nspan = true) => {

  val = Number(val);

  var lang = window.getLanguage(),
      valString = '',
      valNum = val;
  if ( val !== undefined) {
    if ( val < 0 ) {
      valNum *= -1;
    }
    if ( val.toFixed !== undefined ) {
      valString = window.thousandSeparators(valNum.toFixed(2));
    }
  }

  if ( lang === 'fr' ) {
    valString = valString.replace('.', ',')
  }

  if ( val >= 0 ) {

    if ( lang === 'fr' ) {
      return valString + ' $';
    } else {
      return '$' + valString;
    }

  }

  else {

    if ( nspan ) {

      if ( lang === 'fr' ) {
        return '<span class="negative">-' + valString + ' $</span>';
      } else {
        return '<span class="negative">-$' + valString + '</span>';
      }

    }

    else {

      if ( lang === 'fr' ) {
        return '-' + valString + '$';
      } else {
        return '-$' + valString;
      }

    }

  }

}


/**
 * FUNCTION: formatCurrencyShorthand()
 * @param  {string} val - the numeric string to be converted.
 * @param  {boolean} nspan - defines whether negative values should be wrapped in a span.
 * @return {string} the converted string
 *
 * This global function is designed to convert numeric values into currency
 * formatted string, using letter-based shortforms. The function will also optionally 
 * attempt to wrap the entire value in a span, which can be used for visual formatting on 
 * the front-end.
 *
 * The function is currently primarily used in the generation of charts, where full
 * number display is not always possible. 
 *
 * TO DO: This function needs to account for French values.
 */
window.formatCurrencyShorthand = (value, nspan = true) => {

    var newValue = value;

    if (value >= 1000) {
        var suffixes = ["", "K", "M", "B","T"];
        var suffixNum = Math.floor( (""+value).length/3 );
        var shortValue = '';
        for (var precision = 2; precision >= 1; precision--) {
            shortValue = parseFloat( (suffixNum != 0 ? (value / Math.pow(1000,suffixNum) ) : value).toPrecision(precision));
            if ( shortValue < 1 ) {
              shortValue *= 1000;
              suffixNum  -= 1;
            }
            var dotLessShortValue = (shortValue + '').replace(/[^a-zA-Z 0-9]+/g,'');
            if (dotLessShortValue.length <= 2) { break; }
        }

        shortValue = shortValue.toFixed(1);
        newValue = '$'+shortValue+suffixes[suffixNum];
    }
    return newValue;

}

window.formatPercentage = (number, approximate = false) => {

  number = Number(number);
  number *= 100;

  if ( approximate ) {

    if ( number.toFixed(2) > 0 ) {

      return '~' + number.toFixed(2) + "%";

    }

  }

  return number.toFixed(2) + "%";

}

window.padDigits = (number, len) => {

  number += '';

  if ( number.length === 1 ) {

    number = '0' + number.trim();

  }

  return number;

}

window.hexToRGBA = (hex, alpha = '.5') => {
    var r = parseInt(hex.slice(1, 3), 16),
        g = parseInt(hex.slice(3, 5), 16),
        b = parseInt(hex.slice(5, 7), 16);

    if (alpha) {
        return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
    } else {
        return "rgb(" + r + ", " + g + ", " + b + ")";
    }
}


/** 
 * ---------------------------------------------------------------------------- /
 * GLOBAL HELPER FUNCTIONS
 *
 * The following helper functions are intended to be used by the Vue application
 * to access data from and write data to the store. As many of these functions
 * use the eval() function to evaluate assignment expressions (see README.md for
 * details on rationale), we've set these helpers to global functions of the
 * window object so as not to interfere with Vue's own eval() calls.
 *
 * --------------------------------------------------------------------------- */

/**
 * HELPER: _clickOff()
 * @param  {[object]} v - The component instance of the App itself
 *
 * This function is called by the App component, whenever the app itself
 * is clicked. It iterates through the App's component tree and resets
 * the data attribute "active" for any component that has a data attribute
 * of "clickoff".
 *
 * This allows us to define click off functionality within individual
 * components. 
 */
window._clickOff = (v) => {

  for ( var x = 0; x < v.length; x++ ) {

    if ( v[x].$data.clickoff !== undefined ) {

      v[x].$data.active = false;

    }

    if ( v[x].$children.length > 0 ) {

      window._clickOff(v[x].$children);

    }

  }

}


/**
 * HELPER: _dataContext()
 * @param  {Object} state - the active Vuex state
 * @param  {string} field - the field that we'll be accessing
 * @return {various} the value that is retrieved from the Vuex store
 *
 * This function is used to help establish the connection to the desired field within
 * the client data. The primary use is to distinguish between current active data and
 * any snapshot data that may be applied to the client. 
 */
window._dataContext = (state, field) => {

  var c = 'state.client';

  if ( ! field ) { return c; }

  var s = field.split('.'),
      f = s[0];

  if ( ! window._dataContextSegments.includes(f) ) { return c; }

  if ( state.client ) {
    if ( state.client.snapshots ) {
      var t = 'current';
      if ( state.app.report === 'financialgps' ) {
        t = 'gps';
      }
      if ( state.client.snapshots[t] !== 'current' ) {
        c = 'state.client.snapshots.snapshots["' + state.client.snapshots[t] + '"]'
      }
    }
  }

  return c;

}


/**
 * HELPER: _dataContext()
 * @param  {Object} state - the active Vuex state
 * @param  {Object} vars - an object containing data points for the function to work on
 * @return {Object} returns to state to be updated in the Vuex store
 *
 * This function is used to save data to a specific point in the active client object 
 * (as determined by the _dataContext() function).
 */
window._saveField = (state, vars) => {

  if ( vars.field.substr(0, 6) === 'tools.' ) {

    var s  = vars.field.split('.');
    var ex = 'state.tools.' + s[1] + '.' + s[2] + ' = vars.value'; 

  }

  else {
  
    var ex = window._dataContext(state, vars.field) + '.' + vars.field + ' = vars.value';

  }

  eval(ex);

  return state;

}


window._saveSnapshot = (state, data) => {

  if ( Array.isArray(state.client.snapshots.snapshots) ) {
    state.client.snapshots.snapshots = {}
  }
  
  if ( state.client.snapshots.snapshots[data.date] ) {

    if ( ! confirm('Already exists. Overwrite?') ) {

      return state;

    }
    
  }

  state.client.snapshots.snapshots[data.date] = data.snapshot;

  return state;

}


window._deleteSnapshot = (state, data) => {

  if (state.client.snapshots.snapshots[data.date] ) {
    delete state.client.snapshots.snapshots[data.date]
  }

  return state;

}

window._updateSnapshotDate = (state, data) => {

  if (state.client.snapshots.snapshots[data.key] ) {

    var t = (state.app.report === 'financialgps') ? 'gps' : 'current';
    
    if (state.client.snapshots[t] === data.key) {

      state.client.snapshots[t] = data.newkey;

    }

    state.client.snapshots.snapshots[data.newkey] = state.client.snapshots.snapshots[data.key];
    delete state.client.snapshots.snapshots[data.key];
  }

  return state;

}
 

/**
 * HELPER: _saveCell()
 * @param  {Object} state - the active Vuex state
 * @param  {Object} vars - an object containing data points for the function to work on
 * @return {Object} returns to state to be updated in the Vuex store
 *
 * This function is used to save data to a specific 'cell' within a repeater field in
 * the client object (as determined by the _dataContext() function).
 */
window._saveCell = (state, vars) => {

  // Get the repeater row
  var item = {},
      index = -1,
      context = window._dataContext(state, vars.field);

  if ( vars.setting === true ) {

    context = 'state.constants';

  }

  var ex = 'item = ' + context + '.' + vars.field + '.rows[vars.row];';
  eval(ex);

  for ( var x = 0; x < item.length; x++ ) {

    if ( item[x].field === vars.key ) {

      index = x;

    }

  }

  // If the index is still negative 1, we cannot find the required value,
  // and will need to ADD to it. 
  if ( index === -1 ) {

    var entry = {
      field: vars.key,
      type:  vars.type,
      value: vars.value
    };

    ex = context + '.' + vars.field + '.rows[vars.row].push(entry)';
    eval(ex);

  }

  else {

    ex = context + '.' + vars.field + '.rows[vars.row][index].value = "' + vars.value + '"';
    eval(ex);

  }

  return state;

}


/**
 * HELPER: _getFieldData()
 * @param  {Object} state - the active Vuex state
 * @param  {Object} vars - an object containing data points for the function to work on
 * @return {Object} returns to the value of the field.
 *
 * This function is used to pull a field value from the client object in the Veux state
 * (as determined by the _dataContext() function).
 */
window._getFieldData = (state, vars) => {

  // define the data variable
  var data = '';

  // If the field is a part of the tools, set the expression to come from the tools
  if ( vars.field.substr(0, 6) === 'tools.' ) {

    var s  = vars.field.split('.');
    var ex = 'data = state.tools.' + s[1] + '.' + s[2]; 

  }

  // Otherwise, if the client object is not null, set the expression to call from the tools
  else if ( state.client !== false ) {

    var ex = 'data = ' + window._dataContext(state, vars.field) + '.' + vars.field;

  }

  // Execute the expression
  eval(ex);

  // Return the value
  return (data === undefined) ? '' : data;

}


/**
 * HELPER: _getDropdownData()
 * @param  {Object} state - the active Vuex state
 * @param  {Object} vars - an object containing data points for the function to work on
 * @return {Object} returns to the value of the field.
 *
 * This function is used to pull a dropdown field value from the client object in the Veux state
 * (as determined by the _dataContext() function).
 */
window._getDropdownData = (state, vars) => {

  var val   = '',
      data  = window._getFieldData(state, vars),
      proto = state.prototype.dropdown[vars.proto];

  for (var key in proto) {

    if ( data === key ) {

      val = proto[key];

    }

  }

  return val;

}


/**
 * HELPER: _getRepeaterData()
 * @param  {Object} state - the active Vuex state
 * @param  {Object} vars - an object containing data points for the function to work on
 * @return {Object} returns the repeater data object.
 *
 * This function is used to pull repeater field data from the client object in the Veux state
 * (as determined by the _dataContext() function).
 */
window._getRepeaterData = (state, vars) => {

  var data = [],
      context = window._dataContext(state, vars.field);

  if ( vars.spouse === true ) {

    context = 'state.spouse';
    
  }

  if ( vars.setting === 'true' ) {

    context = 'state.constants';

  }

  var ex = 'data = ' + context + '.' + vars.field;
  eval(ex);

  if (data) {
    var ex = 'data = ' + context + '.' + vars.field + '.rows';
  }

  eval(ex);

  return data;

}


/**
 * HELPER: _getRepeaterTotal()
 * @param  {Object} state - the active Vuex state
 * @param  {Object} vars - an object containing data points for the function to work on
 * @return {Number} returns to the value of the field.
 *
 * This function is used to determine the total value of a repeater field in the client data
 * (as determined by the _dataContext() function).
 */
window._getRepeaterTotal = (state, vars) => {

  var data = window._getRepeaterData(state, vars),
      total = 0;

  if ( data.length > 0 ) {

    for ( var x = 0; x < data.length; x++ ) {

      for ( var f = 0; f < data[x].length; f++ ) {

        if ( data[x][f].field === vars.totalby ) {

          total += Number(data[x][f].value);

        }

      }

    }

    return total;

  }    

  return 0;

}


/**
 * HELPER: _getRepeaterLastValue()
 * @param  {Object} state - the active Vuex state
 * @param  {Object} vars - an object containing data points for the function to work on
 * @return {Number} returns to the value of the last value in the repeater field.
 *
 * This function is used to determine the value of a `cell` in the last entry in a repeater
 * within the client data (as determined by the _dataContext() function).
 */
window._getRepeaterLastValue = (state, vars) => {

  var data = window._getRepeaterData(state, vars),
      last = 0;

  if ( data.length > 0 ) {

    var x = data.length - 1;

    for ( var f = 0; f < data[x].length; f++ ) {

      if ( data[x][f].field === vars.totalby ) {

        last = Number(data[x][f].value);

      }

    }

  }

  return last;

}


/**
 * HELPER: _saveRepeaterData()
 * @param  {Object} state - the active Vuex state
 * @param  {Object} vars - an object containing data points for the function to work on
 * @return {Number} returns to the value of the last value in the repeater field.
 *
 * This function is used to determine the value of a `cell` in the last entry in a repeater
 * within the client data (as determined by the _dataContext() function).
 */ 
window._saveRepeaterData = (state, vars) => {

  var context = window._dataContext(state, vars.field);

  if ( vars.setting === 'true' ) {
    context = 'state.constants';

  }

  var ex = context + '.' + vars.field + '.rows = vars.data';
  eval(ex);

  return state;

}


/**
 * HELPER: _getEmptyRow()
 * @param  {Object} state - the active Vuex state
 * @param  {Object} vars - an object containing data points for the function to work on
 * @return {Number} returns the state with the new empty row added to the repeater.
 *
 * This function is used to return an empty row from an array, defining the repeater
 * prototype. It does this by adding an empty row to the repeater field data, which
 * triggers the appearance of the empty row within the component. 
 *
 * The repeater component does include a cancel button, which will restore the data in
 * the field before closing the repeater panel if the user wants to undo this action.
 */ 
window._getEmptyRow = (state, vars) => {

  var context = window._dataContext(state, vars.field);

  if ( vars.setting === 'true' ) {
    
    context = 'state.constants';

  }

  var ex = context + '.' + vars.field + '.rows.push(vars.data)';
  eval(ex);

  return state;

}


/**
 * HELPER: _removeRow()
 * @param  {Object} state - the active Vuex state
 * @param  {Object} vars - an object containing data points for the function to work on
 * @return {Number} returns the state with the target row removed from the repeater.
 *
 * This function is used to remove a row from a repeater field. This is achieved by 
 * dropping the row from the client object, which triggers is disappearance within
 * the repeater component.
 * 
 * The repeater component does include a cancel button, which will restore the data in
 * the field before closing the repeater panel if the user wants to undo this action.
 */ 
window._removeRow = (state, vars) => {

  var context = window._dataContext(state, vars.field);

  if ( vars.setting === 'true' ) {
    context = 'state.constants';

  }

  var ex = context + '.' + vars.field + '.rows.splice(vars.index, 1)';  
  eval(ex);

  return state;
}


/** 
 * ---------------------------------------------------------------------------- /
 * CHART FUNCTIONS
 *
 * The following helper functions are intended to be used by the Vue application
 * to assist in the creation and generation of charts.
 *
 * --------------------------------------------------------------------------- */

/**
 * HELPER: createLegend()
 * @param  {string/boolean} target - 
 * @param  {array} legend - 
 * @param  {string} c - 
 * 
 * This function is used to generate the custom legend HTML used by certain charts
 * throughout the application. It iterates over the data, stores the data to the
 * window.tables.legends array for use later, while simultaneously building out 
 * the HTML elements, which it then actively applies to the target HTML element.
 */

window.createLegend = (target = false, legend = [], c = 'fillStyle') => {

  var l = target,
      html = '',
      u = '[undefined string]',
      lang = window.getLanguage();

  target += '__legend';

  window.tables.legends[l] = [];

  if ( legend.length > 0 ) {

    html += '<ul class="chartLegend">';

    for ( var x = 0; x < legend.length; x++ ) {

      var split = legend[x].text.split('::'),
           vars = false;

      window.tables.legends[l][x] = {
        color: legend[x][c],
        name:  split[0],
      }

      if ( split.length > 1 ) {

          var pairs = split[1].split(',');
          
          for ( var y = 0; y < pairs.length; y++ ) {

            var v = pairs[y].split(':');
                vars = {};

            vars[v[0]] = v[1];
            window.tables.legends[l][x][v[0]] = v[1];

          }

      }

      html += '<li class="chartLegend__item"><span class="chartLegend__color" style="background-color:' + legend[x][c] + '"';

      if ( vars.length !== false ) {

        for ( var k in vars ) {

          html += ' data-' + k + '="' + vars[k] + '"';

        }

      }

      var label = '';

      if ( ! translations.translations[split[0]] ) {
        label = u;
      }
      else {
        var s = translations.translations[split[0]];
        var label = (s[lang]) ? s[lang] : s['en'] + ' [' + lang.toUpperCase() + ']';
      }

      if ( label === u ) {
        label = split[0];
      }

      html += '></span>' + label + '</li>';

    }

    html += '</ul>';

  }

  if (document.getElementById(target) ) {

    document.getElementById(target).innerHTML = html;

  }

}


/** 
 * A simple extention for Chart.js that determines whether or not the chart is 
 * intended for display on the PDF, and changes the background colour accordingly.
 * We're using a dark gradient in the app, but plain white in the PDF.
 */
Chart.plugins.register({
  beforeDraw: function(chartInstance) {
    var ctx = chartInstance.chart.ctx;
    if ( chartInstance.options.forPDF ) {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, chartInstance.chart.width, chartInstance.chart.height);
    }
  }
});


/**
 * Line Shadow
 * This block of code is used to add an additional shadow on the lines in the
 * line charts.
 */
let draw = Chart.controllers.line.prototype.draw;
Chart.controllers.line = Chart.controllers.line.extend({
    draw: function() {
        let ctx = this.chart.chart.ctx;
        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, .2)';
        ctx.shadowBlur = 12;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 5;
        ctx.stroke();
        draw.apply(this, arguments);
        ctx.restore();
    }
});


/**
 * REGISTER VUE
 *
 * At this point, we're ready to actually register the Vue instance to 
 * initialize the app. First, we need to check if our app element is present.
 * If it is, we'll load the app. If it's not, we'll check for the login
 * element to load the login screen. 
 *
 * The PHP on the Apache server will replace the <app> element with a 
 * <login> element if the user is not logged in.
 */


/**
 * So... we'll start by checking for the app tag.
 * If it's not found, we'll set things up to display the login.
 */
var vueTag  = document.getElementsByTagName("app"),
    vueEl   = (vueTag.length > 0) ? 'app' : 'login',
    vueComp = (vueTag.length > 0) ? App : Login;
 

/**
 * Next, we'll tell Vue that we want to include the Axios library.
 * This will be used to interact with the API
 */ 
Vue.use(VueAxios, axios);

if (! /(^|;\s?)PHPSESSID=/.test(document.cookie)) {
 
  vueComp = Login;

}


/**
 * Set up the mixins that should be available to all the Vue components.
 */
Vue.mixin({

  methods: {


    /**
     * FUNCTION: _translate
     * @param  {string} key - this is used to identify the string
     * @return {string} the string to be used
     *
     * This function is used to fetch the desired string from the translation table.
     * It also checks to determine if there is an active translation. If there is, it
     * attempts to return the translation.
     */
    _translate: function(key) {

      var lang = this.$store.getters.getCurrentLanguage;

      if ( ! translations.translations[key] ) {
        return '[undefined string]';
      }
      
      var s = translations.translations[key];
      return (s[lang]) ? s[lang] : s['en'] + ' [' + lang.toUpperCase() + ']';

    },


    /**
     * FUNCTION: _prints
     * @param  {string} string - this is the string to be checked for a substitution
     * @param  {string} s - this is the string that should replace any tokens (%s)
     * @return {string} the string with substitutions applied.
     *
     * This function is used to apply substitutions to strings through the use
     * of a token (%s). The idea is to be able to add dynamic application/client 
     * data to strings that are used primarily as labels.
     */
    _prints: function(string, s) {
      return string.replace('%s', s);
    }

  }

})


/**
 * Initialize the Vue component.
 * We'll use the vueEl and vueComp elements defined above to control
 * the views that Vue loads.
 */
new Vue({

  // Define the Vue element
  el: vueEl,

  // Define the render function, along with the Vue compinent.
  render: h => h(vueComp),

  // Define the mounted function. This will pull in initial data
  // from the API
  mounted: function() {

    var self = this;

    this.axios.get(window.api_uri + 'api/a23jf6x002d500as/init/')
    .then(function (response) {

      self.$store.dispatch('initData', response.data);

    })
    .catch(function (error) {

      window.console.log(error);

    }); 


    // Dev Code!
    // up, up, down, down, left, right, left, right, b, a
    const kode = [38, 38, 40, 40, 37, 39, 37, 39, 66, 65];
    const length = kode.length;
    var pos = 0;

    document.addEventListener('keydown', function (event) {

      if (event.keyCode === kode[pos++]) {

        if (length === pos) {
          
          self.$store.dispatch('updateDevMode', ! self.$store.getters.getDevMode());

          pos = 0; // ability to start over
          return false;

        }

      } 

      else {

        pos = 0;

      }

    });

  },

  // Inject the store and Axios elements into the app (all children)
  // for easy access to their methods.
  store,
  axios,
  
});



// WEBPACK FOOTER //
// ./src/main.js